import { useEffect, useState } from 'react';
import { LoginSplit } from './features/login/LoginSplit';
import { Hub } from './features/hub/Hub';
import { LaunchOverlay } from './features/hub/LaunchOverlay';
import { AccessDenied } from './features/access/AccessDenied';
import { LoadingSplash } from './features/access/LoadingSplash';
import type { Tool } from './features/hub/tools';
import { useNexusAuthorization } from './lib/useNexusAuthorization';
import { deriveHubUser } from './lib/auth';

const ACCENT = '#FF7A59';

export default function App() {
  const { user, decision, loading, error, signOut } = useNexusAuthorization();
  const [launching, setLaunching] = useState<Tool | null>(null);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent', ACCENT);
  }, []);

  const handleLaunch = (tool: Tool) => {
    setLaunching(tool);
    if (tool.href) {
      // Open in a new tab so the hub stays put. noopener + noreferrer
      // prevent the launched site from tampering with this window and
      // strip the referrer header.
      window.open(tool.href, '_blank', 'noopener,noreferrer');
      // Brief confirmation, then clear — long enough to register the
      // click, short enough that returning to the hub doesn't sit on
      // a "launching…" spinner.
      setTimeout(() => setLaunching(null), 800);
      return;
    }
    setTimeout(() => setLaunching(null), 1800);
  };

  // 1. Signed out — show login. (Initial loading=true with no user lands here briefly.)
  if (!user) {
    return <LoginSplit accent={ACCENT} />;
  }

  // 2. Signed in, authorize in flight — show splash to avoid a flash of login.
  if (loading && !decision) {
    return <LoadingSplash message="Checking with Nexus…" accent={ACCENT} />;
  }

  // 3. Authorize failed (network, 5xx, misconfiguration).
  if (error || !decision) {
    return (
      <AccessDenied
        decision={{
          allowed: false,
          reason: null,
          message: error,
          uid: null,
          email: user.email,
          display_name: user.displayName,
          org_id: null,
          nexus_roles: [],
          app_roles: [],
          granted_permissions: [],
          team_ids: [],
          client_id: null,
          profile: null,
          cache_ttl_seconds: 0,
          issued_at: null,
          expires_at: null,
        }}
        onSignOut={() => void signOut()}
        accent={ACCENT}
      />
    );
  }

  // 4. Denied by Nexus.
  if (!decision.allowed) {
    return <AccessDenied decision={decision} onSignOut={() => void signOut()} accent={ACCENT} />;
  }

  // 5. Allowed — render the hub.
  const hubUser = deriveHubUser(decision);
  return (
    <>
      <Hub
        user={hubUser}
        appRoles={decision.app_roles}
        accent={ACCENT}
        onSignOut={() => void signOut()}
        onLaunch={handleLaunch}
      />
      {launching && <LaunchOverlay tool={launching} accent={ACCENT} />}
    </>
  );
}
